import { AuthenticatedRealtimeService } from "../../shared/services/authenticated-realtime.service";
import { switchMap, takeUntil, catchError, debounceTime } from "rxjs/operators";
import {formatDate} from "@angular/common";
import {Component, HostListener, OnDestroy, OnInit} from "@angular/core";
import {BehaviorSubject, Subject, EMPTY} from "rxjs";
import {PlayerService} from "../../shared/services/player.service";
import {Chat} from "../../shared/models/chat";
import {Player} from "app/shared/models/player";
import {ActivatedRoute, Router} from "@angular/router";
import {ChatService} from "../../shared/services/chat.service";
import { DelayedSpinnerService } from "../../shared/services/delayed-spinner.service";
import {TranslateService} from "@ngx-translate/core";
import {LocaleService} from "../../shared/i18n/locale.service";

type ChatListFilter = "all" | "unread" | "favorites" | "groups";



@Component({
    selector: 'chat-list',
    templateUrl: './chat-list.component.html',
    styleUrls: ['../chat.component.css'],
    standalone: false
})
export class ChatListComponent implements OnInit, OnDestroy {

  private stopRealtime: () => void;
  private readonly destroy$ = new Subject<void>();
  private readonly refresh$ = new Subject<void>();

  public chats: Chat[] = [];
  public loading = true;
  public loadingMoreChats = false;
  public hasMoreChats = true;
  public activeFilter: ChatListFilter = "all";
  public readonly chatsReady$ = new BehaviorSubject<boolean>(false);

  private currentPlayer: Player;
  private readonly resultsPerPage = 30;
  private nextBeforeActivity: number = null;
  private listRevision = 0;

  constructor(private chatService: ChatService,
              private playerService: PlayerService,
              private router: Router,
              private route: ActivatedRoute,
              private spinner: DelayedSpinnerService,
              private translate: TranslateService,
              private localeService: LocaleService, private realtime: AuthenticatedRealtimeService) {
  }

  ngOnInit() {
    this.currentPlayer = this.playerService.getCurrentPlayer();
    this.refresh$.pipe(debounceTime(150), switchMap(() => this.chatService.getChatWindow(Math.max(this.resultsPerPage, this.chats.length)).pipe(catchError(() => EMPTY))), takeUntil(this.destroy$)).subscribe(page => {
      if (this.currentPlayer?.id !== this.playerService.getCurrentPlayer()?.id) return;
      ++this.listRevision;
      this.loadingMoreChats = false;
      this.chats = this.calculateDisplayNames(page.items);
      this.nextBeforeActivity = page.nextBeforeActivity;
      this.hasMoreChats = page.hasMore;
    });
    this.stopRealtime = this.realtime.subscribe(({type}) => {
      if (['connected', 'chat.message', 'chat.updated', 'manager.announcement.changed', 'participants.changed', 'event.deleted'].includes(type)) this.refresh$.next();
    });
    this.showLoadingSpinner();

    this.currentPlayer = this.playerService.getCurrentPlayer();
    this.playerService.checkCity();
    const prepared = this.route.snapshot.data['screen'];
    if (prepared) {
      this.chats = this.calculateDisplayNames(prepared.items || []);
      this.nextBeforeActivity = prepared.nextBeforeActivity;
      this.hasMoreChats = prepared.hasMore;
      this.loading = false;
      this.hideLoadingSpinner();
      this.chatsReady$.next(true);
    } else this.loadChats(true);
  }

  ngOnDestroy(): void {
    this.hideLoadingSpinner();
    this.stopRealtime?.();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public createNewChat(): void {
    this.router.navigate([
      '/chat-detail',
      {
        newChat: true
      }
    ]);
  }

  public enterChat(chat: Chat): void {
    this.router.navigate(['/messages', {chatId: chat.id}]);
  }

  public setFilter(filter: ChatListFilter): void {
    this.activeFilter = filter;
  }

  @HostListener("window:scroll", [])
  public onWindowScroll(): void {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight
    );

    if (documentHeight - (scrollTop + viewportHeight) < 240) {
      this.loadChats(false);
    }
  }

  public filteredChats(): Chat[] {
    return (this.chats || []).filter(chat => {
      switch (this.activeFilter) {
        case "unread":
          return !!chat.hasNewMessages;
        case "favorites":
          return !!(chat as any).favorite;
        case "groups":
          return this.isGroupChat(chat);
        case "all":
        default:
          return true;
      }
    });
  }

  public getChatInitials(chat: Chat): string {
    const value = chat && chat.displayName ? chat.displayName : chat && chat.name ? chat.name : "";
    const parts = value.trim().split(/\s+/).filter(part => !!part);
    if (parts.length === 0) {
      return "?";
    }
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  public getChatAvatarUrl(chat: Chat): string {
    const player = this.getDirectChatPlayer(chat);
    return player && player.avatar ? player.avatar : null;
  }

  public getGroupCenterPlayer(chat: Chat): Player {
    if (!chat) {
      return null;
    }
    if (chat.contact && chat.contact.id) {
      return chat.contact;
    }
    const players = chat.players || [];
    return players.find(player => player && player.id && (!this.currentPlayer || player.id !== this.currentPlayer.id)) ||
      players.find(player => player && player.id) ||
      null;
  }

  public getGroupSatellitePlayers(chat: Chat): Player[] {
    const center = this.getGroupCenterPlayer(chat);
    const centerId = center && center.id;
    return (chat && chat.players ? chat.players : []).filter(player =>
      !!(player && player.id && player.id !== centerId)
    );
  }

  public getPlayerInitials(player: Player): string {
    const value = player ? (player.nickName || player.name || "") : "";
    const parts = value.trim().split(/[\s._-]+/).filter(part => !!part);
    if (parts.length === 0) {
      return "?";
    }
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  public getChatActivityLabel(chat: Chat): string {
    const activityDate = this.getChatActivityDate(chat);
    if (!activityDate) {
      return "";
    }

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfToday.getDate() - 1);

    const startOfWeekWindow = new Date(startOfToday);
    startOfWeekWindow.setDate(startOfToday.getDate() - 6);

    const locale = this.localeService.currentLanguage === "hr" ? "hr-HR" : "en-US";
    if (activityDate >= startOfToday) {
      return formatDate(activityDate, "H:mm", locale);
    }
    if (activityDate.getFullYear() !== startOfToday.getFullYear()) {
      return formatDate(activityDate, "d.M.y", locale);
    }
    if (activityDate >= startOfYesterday) {
      return this.translate.instant("chat.yesterday");
    }
    if (activityDate >= startOfWeekWindow) {
      return formatDate(activityDate, "EEEE", locale);
    }
    return formatDate(activityDate, "d.M.", locale);
  }

  private loadChats(reset: boolean): void {
    if (this.loadingMoreChats || (!reset && !this.hasMoreChats)) {
      return;
    }
    if (reset) {
      this.loading = true;
      this.hasMoreChats = true;
      this.nextBeforeActivity = null;
    } else {
      this.loadingMoreChats = true;
    }

    const revision = this.listRevision;
    this.chatService.getChatPage(reset ? null : this.nextBeforeActivity, this.resultsPerPage).pipe(takeUntil(this.destroy$)).subscribe(page => {
      if (revision !== this.listRevision || this.currentPlayer?.id !== this.playerService.getCurrentPlayer()?.id) return;
      const pageChats = this.calculateDisplayNames(page.items || []);
      this.chats = reset ? pageChats : this.appendChats(this.chats, pageChats);
      this.nextBeforeActivity = page.nextBeforeActivity;
      this.hasMoreChats = page.hasMore;
      this.loading = false;
      this.loadingMoreChats = false;
      this.hideLoadingSpinner();
      this.chatsReady$.next(true);
    }, response => {
      if (revision !== this.listRevision) return;
      this.loading = false;
      this.loadingMoreChats = false;
      this.hideLoadingSpinner();
      this.chatsReady$.next(true);
    });
  }

  private appendChats(existingChats: Chat[], newChats: Chat[]): Chat[] {
    const chatsById = new Map<string, Chat>();
    (existingChats || []).forEach(chat => {
      if (chat && chat.id) {
        chatsById.set(chat.id, chat);
      }
    });
    (newChats || []).forEach(chat => {
      if (chat && chat.id && !chatsById.has(chat.id)) {
        chatsById.set(chat.id, chat);
      }
    });
    return Array.from(chatsById.values());
  }

  private calculateDisplayNames(chats: Chat[]) : Chat[]{
    
    chats.forEach(chat => {
      this.chatService.calculateDisplayName(chat, this.currentPlayer.id);
    });

    return chats;
  }

  public isGroupChat(chat: Chat): boolean {
    return !!(chat && (chat.entityType === "EVENT" || (chat.players && chat.players.length > 2)));
  }

  private getDirectChatPlayer(chat: Chat): Player {
    if (!chat || chat.entityType !== "PLAYER" || !chat.players || chat.players.length !== 2 || !this.currentPlayer) {
      return null;
    }
    return chat.players.find(player => player && player.id !== this.currentPlayer.id) || null;
  }

  private getChatActivityDate(chat: Chat): Date | null {
    const latestMessage = chat && chat.messages && chat.messages.length > 0 ? chat.messages[0] : null;
    const value = latestMessage && latestMessage.created ? latestMessage.created : chat && chat.lastMessageDate;
    if (!value) {
      return null;
    }

    const activityDate = new Date(value);
    return Number.isNaN(activityDate.getTime()) ? null : activityDate;
  }

  private showLoadingSpinner(): void {
    this.spinner.show();
  }

  private hideLoadingSpinner(): void {
    this.spinner.hide();
  }



  
}
